import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download, Eye, EyeOff } from "lucide-react";
import { generateLegalDoc, generateLegalDocPdf, today, type DocSection } from "./legalDocUtils";
import { LegalDocPreview } from "./LegalDocPreview";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { LegalField } from "./LegalField";
import { wholeYearsMessage, yearsText } from "./amountWords";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Generates a general-purpose Non-Disclosure Agreement template for situations where you need to share confidential information with another party.`,
  how: `Fill in the parties involved, the scope of confidential information, and the term. Preview the document, then download it as a Word or PDF file.`,
  tip: `This is a general starting template — for anything involving significant intellectual property or a high-stakes business relationship, having a lawyer review the specific terms before signing is worth doing.`,
};

const TITLE = "Mutual Non-Disclosure Agreement";

export default function NdaGenerator() {
  const [partyA, setPartyA] = useState("");
  const [partyB, setPartyB] = useState("");
  const [purpose, setPurpose] = useState("evaluating a potential business relationship");
  const [durationYears, setDurationYears] = useState("2");
  const [survivalYears, setSurvivalYears] = useState("3");
  const [state, setState] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const durationMsg = wholeYearsMessage(durationYears, "Disclosure period");
  const survivalMsg = wholeYearsMessage(survivalYears, "Confidentiality period");
  const missingRequired = !partyA.trim() || !partyB.trim() || !state.trim() || !!durationMsg || !!survivalMsg;

  const buildSections = (): DocSection[] => {
    const a = partyA || "[Party A Name]";
    const b = partyB || "[Party B Name]";
    const jurisdiction = state || "[State/Country]";
    return [
      {
        paragraphs: [
          `This Mutual Non-Disclosure Agreement ("Agreement") is entered into as of ${today()}, by and between ${a} ("Party A") and ${b} ("Party B"), collectively referred to as the "Parties," for the purpose of ${purpose}.`,
        ],
      },
      {
        heading: "1. Confidential Information",
        paragraphs: [
          "\"Confidential Information\" means any non-public information disclosed by either Party to the other, whether orally, in writing, or in any other form, that is designated as confidential or that reasonably should be understood to be confidential given the nature of the information and the circumstances of disclosure.",
        ],
      },
      {
        heading: "2. Obligations",
        paragraphs: [
          "Each Party agrees to: (a) hold the other Party's Confidential Information in strict confidence; (b) not disclose such information to any third party without prior written consent; and (c) use the Confidential Information solely for the purpose stated above.",
        ],
      },
      {
        heading: "3. Exclusions",
        paragraphs: [
          "Confidential Information does not include information that: (a) is or becomes publicly available through no fault of the receiving Party; (b) was already known to the receiving Party prior to disclosure; (c) is independently developed without use of the disclosing Party's Confidential Information; or (d) is required to be disclosed by law or court order.",
        ],
      },
      {
        heading: "4. Term and Survival",
        paragraphs: [
          `This Agreement covers disclosures made during ${yearsText(Number(durationYears) || 0)} from the date first written above (the "Disclosure Period"), unless terminated earlier by either Party on written notice. The Parties' confidentiality obligations continue for ${yearsText(Number(survivalYears) || 0)} after the end of the Disclosure Period or the date of termination, whichever occurs first. Obligations for any Confidential Information that is a trade secret under applicable law continue for as long as it remains a trade secret.`,
        ],
      },
      {
        heading: "5. Return or Destruction",
        paragraphs: [
          "Within thirty (30) days after a written request or the end of the Disclosure Period, the receiving Party shall return or destroy all Confidential Information of the disclosing Party, including copies, and confirm this in writing on request. A Party may keep copies it is legally required to retain or that sit in routine backups, which remain subject to this Agreement.",
        ],
      },
      {
        heading: "6. Compelled Disclosure",
        paragraphs: [
          "If the receiving Party is required by law or court order to disclose Confidential Information, it shall, where legally permitted, give the disclosing Party prompt written notice so that the disclosing Party may seek a protective order, and shall disclose only the portion legally required.",
        ],
      },
      {
        heading: "7. No License; No Warranty",
        paragraphs: [
          "All Confidential Information remains the property of the disclosing Party. Nothing in this Agreement grants a license to any patent, copyright, trade secret or other intellectual property right. Confidential Information is provided \"as is\".",
        ],
      },
      {
        heading: "8. Remedies",
        paragraphs: [
          "Each Party acknowledges that unauthorized use or disclosure of Confidential Information may cause harm for which money damages alone would not be adequate, and that the disclosing Party may seek injunctive relief in addition to any other remedy available at law or in equity.",
        ],
      },
      {
        heading: "9. Protected Disclosures",
        paragraphs: [
          "Under the U.S. Defend Trade Secrets Act (18 U.S.C. § 1833(b)), an individual is not liable under any federal or state trade secret law for disclosing a trade secret in confidence to a government official or to an attorney solely to report or investigate a suspected violation of law, or in a complaint or other document filed under seal in a lawsuit or other proceeding. Nothing in this Agreement prohibits reporting possible violations of law to a government agency.",
        ],
      },
      {
        heading: "10. Governing Law",
        paragraphs: [`This Agreement shall be governed by the laws of ${jurisdiction}, without regard to its conflict-of-laws rules. This Agreement is the entire agreement on its subject and may be changed only in a writing signed by both Parties.`],
      },
      {
        heading: "Signatures",
        paragraphs: [
          "Party A: _________________________  Date: _____________",
          "Party B: _________________________  Date: _____________",
        ],
      },
    ];
  };

  // Was useLogToolUsage(true, 3000, { ownActions: true }) -- a debounced logger with no
  // gating at all, while the real value-delivering actions (the Word/
  // PDF download buttons below) had ZERO usage-limit check of any
  // kind, not even the weak kind -- confirmed directly: clicking
  // either button at the daily limit generated and downloaded a real,
  // complete document. logUsageNow() gates each button's own click
  // directly, same pattern as Merge PDF's own discrete action.
  const { logUsageNow } = useLogUsageOnAction();


  return (
    <ToolContainer>
        <div className="max-w-md mx-auto space-y-4">
          <LegalField label="Party A Name *"><Input value={partyA} onChange={e => setPartyA(e.target.value)} placeholder="e.g. Acme Inc." /></LegalField>
          <LegalField label="Party B Name *"><Input value={partyB} onChange={e => setPartyB(e.target.value)} placeholder="e.g. Jane Smith" /></LegalField>
          <LegalField label="Purpose of Disclosure"><Input value={purpose} onChange={e => setPurpose(e.target.value)} /></LegalField>
          <LegalField label="Disclosure period (years)" error={durationMsg}><Input type="number" inputMode="numeric" min="1" max="99" value={durationYears} onChange={e => setDurationYears(e.target.value)} /></LegalField>
          <LegalField label="Confidentiality continues after that (years)" error={survivalMsg}><Input type="number" inputMode="numeric" min="1" max="99" value={survivalYears} onChange={e => setSurvivalYears(e.target.value)} /></LegalField>
          <LegalField label="Governing State/Country *"><Input value={state} onChange={e => setState(e.target.value)} placeholder="e.g. California, USA" /></LegalField>
          {missingRequired && (
            <NoticeBox className="text-xs">Fields marked * are required before you can preview or download.</NoticeBox>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPreview(v => !v)} disabled={missingRequired} className="flex-1 gap-2">
              {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {showPreview ? "Hide Preview" : "Preview"}
            </Button>
          </div>

          {showPreview && !missingRequired && (
            <LegalDocPreview title={TITLE} sections={buildSections()} />
          )}

          <div className="flex gap-2">
            <Button onClick={async () => { const allowed = await logUsageNow(); if (!allowed) return; generateLegalDoc(TITLE, buildSections(), "mutual-nda.docx"); }} disabled={missingRequired} className="flex-1 gap-2">
              <Download className="h-4 w-4" /> Word
            </Button>
            <Button onClick={async () => { const allowed = await logUsageNow(); if (!allowed) return; generateLegalDocPdf(TITLE, buildSections(), "mutual-nda.pdf"); }} disabled={missingRequired} variant="secondary" className="flex-1 gap-2">
              <Download className="h-4 w-4" /> PDF
            </Button>
          </div>
        </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} disclaimer="This tool generates a general-purpose non-disclosure agreement template. It isn't legal advice and may not account for the specific laws of your state, country, or situation — requirements for this type of non-disclosure agreement can vary significantly by jurisdiction. Have any document reviewed by a qualified attorney before you sign it or rely on it, especially for anything involving significant money, property, or legal obligations." />
    </ToolContainer>
  );
}
